// Third-party
import round from 'lodash/round';

// Utils
import { deepClone, uniqueArray } from '../objects';
import { CURRENCY } from '../currency';
import { COMPARISON_ITEM_TYPES, Interval } from '../constants';
import { portfolioHasMonthlyData, getHoldingWithEarliestEnding, rollingPerformance, portfolioHoldingsToComparisonHoldings } from '../portfolio/utils';
import calculateRiskMetrics from '../portfolio/risk-metrics';
import {
  calculateCalendarYearReturns,
  calculatePercentGrowth,
  calculateUpDownMonths,
  calculateBestWorstPerformance,
  initRiskData,
  calculateTrailingReturns,
  calculateCalendarYearFromRolling,
  calculatePerformanceCommonInception,
} from '../calculation-utils';
import { generateBSONId } from '../random';
import { formatLocalizedString, formatMessage, LOCALE } from '../i18n';
import { HOLDING_TYPES, setDefaultAllocationMode } from '../portfolios';
import { HoldingAllocationMode } from '@/enums/Portfolio';
import { recalculateHoldingAmountsFromUnits } from '../portfolio/calculate';
import Dictionary from '@/types/dictionary';
import Nullable from '@/types/nullable';
import ComparisonHolding from '@/types/comparison-holding';
import BenchmarkHolding from '@/types/benchmark-holding';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import Benchmark from '@/types/benchmark';
import Portfolio from '@/types/portfolio';
import PortfolioRisk from '@/types/portfolio-risk';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import PortfolioHolding from '@/types/portfolio-holding';
import Locale from '@/types/locale';
import CommonInception from '@/types/common-inception';
import calculatePortfolioRiskMetrics from '../portfolio/risk-metrics';
import { getRiskFreeBenchmarks } from '@/redux/modules/comparison-benchmarks/comparison-benchmarks-store';
import { Dispatch } from '@/redux';

export const getTotalBenchmarkAllocation = (holdings: (ComparisonHolding | BenchmarkHolding)[]) => {
  const totalWeight = holdings.reduce((total, holding) => total + holding.weight, 0);
  return Math.round(totalWeight * 100000) / 100000;
};

export const benchmarkIsFull = (holdings: (ComparisonHolding | BenchmarkHolding)[]): boolean =>
  holdings.every(h => h.weight > 0) && round(getTotalBenchmarkAllocation(holdings), 2) === 100;

export const comparisonBenchmarkToBenchmark = (comparisonBenchmark: ComparisonBenchmark) =>
  ({
    _id: comparisonBenchmark._id,
    currency: comparisonBenchmark.currency,
    names: {
      full: comparisonBenchmark.name,
      short: comparisonBenchmark.name,
    },
    lipperId: comparisonBenchmark._id,
    performance: {
      ...comparisonBenchmark.performance,
    },
    dates: comparisonBenchmark.dates,
  }) as Benchmark;

export const getBenchmarkCurrency = (benchmark: BenchmarkPortfolio) => {
  const benchmarkCurrencies = uniqueArray(benchmark.holdings.filter(h => h.weight > 0).map(h => h.currency));
  return benchmarkCurrencies.length === 1 ? benchmarkCurrencies[0] : CURRENCY.MIXED;
};

export const benchmarkHoldingsToComparisonHoldings = (holdings: BenchmarkHolding[], benchmarks?: ComparisonBenchmark[], riskFree?: boolean) =>
  holdings.reduce((acc: ComparisonHolding[], curr) => {
    const id = riskFree ? curr.riskFreeId : curr.id;
    if (id) {
      const benchmarkData = benchmarks && benchmarks.find(item => item._id === id);

      const rolling = benchmarkData && benchmarkData.performance.rolling;
      let baseRolling = rolling;
      if (benchmarkData && benchmarkData.currency === CURRENCY.USD) {
        baseRolling = benchmarkData.performance.rollingUSD;
      }

      const holding: ComparisonHolding = {
        name: curr.name,
        weight: curr.weight,
        id: curr.id,
        symbol: curr.symbol,
        riskFreeId: curr.riskFreeId,
        currency: curr.currency,
        rolling,
        baseRolling,
        type: COMPARISON_ITEM_TYPES.BENCHMARK,
      };
      acc.push(holding);
    }
    return acc;
  }, []) as ComparisonHolding[];

/**
 * Get benchmark portfolio dates
 * @param {ComparisonBenchmarks[]} benchmarks the comparison benchmarks in benchmark portfolio
 */
export const getBenchmarkPortfolioDates = (benchmarks: ComparisonBenchmark[]) => {
  let endDate: Nullable<number> = null;

  if (portfolioHasMonthlyData(benchmarks)) {
    const holding = getHoldingWithEarliestEnding(benchmarks);
    endDate = holding.performance.rolling.monthly[holding.performance.rolling.monthly.length - 1][0];
  }

  const dates = {
    growth: {} as Dictionary<Nullable<Date>>,
    risk: {
      lastCalculated: new Date(),
      endDate: endDate ? new Date(endDate) : null,
    },
  };

  const hasClosedHoldings = [...benchmarks].some(h => !!h.inactive);

  if (!hasClosedHoldings) {
    [...benchmarks].forEach(h => {
      dates.growth[h._id] = h.dates.growth ? h.dates.growth : null;
    });
  }

  return dates;
};

/**
 * Calculate benchmark portfolio stats
 * @param {BenchmarkPortfolio} benchmarkPortfolio the comparison benchmarks in benchmark portfolio
 */
export const calculateBenchmarkPortfolioStats = async (
  dispatch: Dispatch,
  benchmarkPortfolio: BenchmarkPortfolio,
  data: ComparisonBenchmark[],
  forcedMonthlyRolling: boolean,
  shortHistory?: boolean,
  commonInception?: CommonInception,
): Promise<BenchmarkPortfolio> => {
  const portfolio = {
    name: {
      [LOCALE.EN_CA]: benchmarkPortfolio.name,
      [LOCALE.FR_CA]: benchmarkPortfolio.name,
    },
    dates: benchmarkPortfolio.dates,
    holdings: benchmarkPortfolio.holdings.map(h => ({
      original: {
        type: 'benchmark',
        id: h.id,
      },
      weight: h.weight,
      name: h.name,
      symbol: h.symbol,
      dollars: 0,
    })),
  } as Portfolio;
  const holdings = portfolioHoldingsToComparisonHoldings(portfolio.holdings, data);
  const effectiveData: ComparisonBenchmark[] = data.filter(datum => holdings.some(item => item.id === datum._id));

  const hasMonthly = portfolioHasMonthlyData(effectiveData);
  // no discretionaryMer or taxes on benchmark portfolios
  const monthlyRollings = rollingPerformance(holdings, Interval.Monthly, 0, undefined, shortHistory);

  const riskFreeBenchmarks = (await dispatch(getRiskFreeBenchmarks(benchmarkPortfolio.holdings))) as any[];

  const risk = hasMonthly
    ? calculatePortfolioRiskMetrics(portfolio, monthlyRollings, effectiveData, riskFreeBenchmarks, false, false, shortHistory, commonInception)
    : (initRiskData(true) as PortfolioRisk);
  const benchmarkDates = getBenchmarkPortfolioDates(effectiveData);

  const percentGrowth = calculatePercentGrowth(portfolio, effectiveData, 0, shortHistory);
  if (commonInception) {
    percentGrowth.CI = calculatePerformanceCommonInception(monthlyRollings, commonInception?.startTimestamp);
  }

  const out: BenchmarkPortfolio = {
    ...benchmarkPortfolio,
    performance: {
      ...benchmarkPortfolio.performance,
      // no taxes on benchmark portfolios
      calendarYear: forcedMonthlyRolling
        ? calculateCalendarYearFromRolling(monthlyRollings)
        : calculateCalendarYearReturns(portfolio, effectiveData, 0, shortHistory),
      percentGrowth: forcedMonthlyRolling ? calculateTrailingReturns(monthlyRollings) : calculatePercentGrowth(portfolio, effectiveData, 0, shortHistory),
      upDownMonths: calculateUpDownMonths(monthlyRollings.map(mr => mr[1])),
      bestWorst: calculateBestWorstPerformance(monthlyRollings),
      risk,
    },
    dates: {
      ...benchmarkPortfolio.dates,
      risk: benchmarkDates.risk,
      growth: benchmarkDates.growth,
    },
  };

  const baseData = deepClone(effectiveData);
  for (const baseDatum of baseData) {
    if (baseDatum.currency === CURRENCY.USD && baseDatum.performance.rollingUSD && baseDatum.performance.percentGrowthUSD) {
      baseDatum.performance.rolling = baseDatum.performance.rollingUSD;
      baseDatum.performance.percentGrowth = baseDatum.performance.percentGrowthUSD;
      baseDatum.performance.calendarYear = baseDatum.performance.calendarYearUSD;
    }

    const baseHasMonthly = portfolioHasMonthlyData(baseData);
    // no discretionaryMer or taxes on benchmark portfolios
    const baseRollings = rollingPerformance(holdings, Interval.Monthly, 0, undefined, shortHistory);

    const baseRisk = baseHasMonthly
      ? calculatePortfolioRiskMetrics(portfolio, baseRollings, baseData, riskFreeBenchmarks, false, false, shortHistory, commonInception)
      : (initRiskData(true) as PortfolioRisk);

    const percentGrowthBase = forcedMonthlyRolling ? calculateTrailingReturns(baseRollings) : calculatePercentGrowth(portfolio, baseData, 0, shortHistory);
    if (commonInception) {
      percentGrowthBase.CI = calculatePerformanceCommonInception(baseRollings, commonInception?.startTimestamp);
    }

    const performanceBase = {
      ...benchmarkPortfolio.performance,
      // no taxes on benchmark portfolios
      calendarYear: forcedMonthlyRolling ? calculateCalendarYearFromRolling(baseRollings) : calculateCalendarYearReturns(portfolio, baseData, 0, shortHistory),
      percentGrowth: percentGrowthBase,
      upDownMonths: calculateUpDownMonths(baseRollings.map(mr => mr[1])),
      bestWorst: calculateBestWorstPerformance(baseRollings),
      risk: baseRisk,
    };

    out.performanceBase = performanceBase;
  }

  const holdingsCopy = deepClone(holdings);
  for (const holding of holdingsCopy) {
    holding.rolling = holding.baseRolling;
  }

  const baseHasMonthly = portfolioHasMonthlyData(baseData);
  // no discretionaryMer or taxes on benchmark portfolios
  const baseRollings = rollingPerformance(holdingsCopy, Interval.Monthly, 0, undefined, shortHistory);

  const baseRisk = baseHasMonthly
    ? calculateRiskMetrics(portfolio, baseRollings, baseData, riskFreeBenchmarks, false, false, shortHistory, commonInception)
    : (initRiskData(true) as PortfolioRisk);

  const performanceBase = {
    ...benchmarkPortfolio.performance,
    // no taxes on benchmark portfolios
    calendarYear: forcedMonthlyRolling ? calculateCalendarYearFromRolling(baseRollings) : calculateCalendarYearReturns(portfolio, baseData, 0, shortHistory),
    percentGrowth: forcedMonthlyRolling ? calculateTrailingReturns(baseRollings) : calculatePercentGrowth(portfolio, baseData, 0, shortHistory),
    upDownMonths: calculateUpDownMonths(baseRollings.map(mr => mr[1])),
    bestWorst: calculateBestWorstPerformance(baseRollings),
    risk: baseRisk,
  };

  out.performanceBase = performanceBase;

  return out;
};

/**
 * Creates a category benchmark based on a portfolio's holdings and weights
 * @param {Dictionary<number>} benchmarksMap map of comparison benchmark ids and weights
 * @param {ComparisonBenchmark[]} benchmarks array of comparison benchmarks containing all category benchmarks for holdings
 * @param {Portfolio} portfolio Original portfolio to base category benchmark off of
 */
export const createPortfolioCategoryBenchmark = (
  benchmarksMap: Dictionary<number>,
  benchmarks: ComparisonBenchmark[],
  portfolio: Portfolio,
): BenchmarkPortfolio => {
  const benchmarkIds = Object.keys(benchmarksMap);

  const benchmarkHoldings = benchmarkIds
    .map(id => {
      const benchmark = benchmarks.find(b => b._id === id);

      if (!benchmark) {
        return null;
      }

      const weight = benchmarksMap[id];

      return {
        type: COMPARISON_ITEM_TYPES.BENCHMARK,
        id: benchmark._id,
        name: benchmark.name || '',
        symbol: benchmark.ticker || '',
        weight,
        currency: benchmark.currency || '',
        riskFreeId: benchmark.riskFreeId,
      };
    })
    .filter(h => !!h) as BenchmarkHolding[];

  return {
    _id: generateBSONId(),
    name: formatMessage('HoldingsEditor.NewName.CategoryBenchmark', { name: formatLocalizedString(portfolio.name) }),
    holdings: benchmarkHoldings,
    dates: {},
  };
};

/**
 * Creates a category benchmarks map. If a portfolio is passed in, then the portfolio holdings/weights will be used to generate the map
 * @param {{ funds: Fund[], stocks: Stock[] }} data Object containing fund and stock perfomance data
 * @param {Portfolio} portfolio Original portfolio
 */
export const getCategoryBenchmarksMap = (data: { funds: Fund[]; stocks: Stock[] }, portfolio?: Portfolio) => {
  const map = {} as Dictionary<number>;
  const { funds, stocks } = data;

  // Fund
  if (!portfolio && funds.length === 1 && funds[0].categoryBenchmarkId) {
    map[funds[0].categoryBenchmarkId] = 100;
  }

  // Stock
  if (!portfolio && stocks.length === 1) {
    map[stocks[0].benchmarkId] = 100;
  }

  // Portfolio
  if (portfolio) {
    const calculateWeight = (holding: PortfolioHolding, benchmarkId: string) => {
      let weight = 0;

      // If a holding with the same benchmark was already added to the map
      // set the weight of this iteration to this previously added weight value
      // effectively "combining" the weights of all holdings that share the same benchmark
      if (map[benchmarkId]) {
        weight = map[benchmarkId];
      }

      weight += holding.weight;

      return weight;
    };

    let processedPortfolio = setDefaultAllocationMode(portfolio);
    if (processedPortfolio.allocationMode === HoldingAllocationMode.Units) {
      // If the processed portfolio's allocationMode is UNITS
      // We can calculate the percentage weight using those unit values and each holding's current price
      // updating the portfolio accordingly
      processedPortfolio = recalculateHoldingAmountsFromUnits(portfolio, [...funds, ...stocks]);
    }

    for (const holding of processedPortfolio.holdings) {
      if (holding.original.type === HOLDING_TYPES.FUND && funds) {
        const fund = funds.find(f => f._id === holding.original.id);
        if (fund && fund.categoryBenchmarkId) {
          map[fund.categoryBenchmarkId] = calculateWeight(holding, fund.categoryBenchmarkId);
        }
      }

      if (holding.original.type === HOLDING_TYPES.STOCK && stocks) {
        const stock = stocks.find(f => f._id === holding.original.id);

        if (stock != null && stock.benchmarkId != null) {
          map[stock.benchmarkId] = calculateWeight(holding, stock.benchmarkId);
        }
      }
    }
  }

  return map;
};

/**
 * Converts a single comparison benchmark to a benchmark portfolio
 * @param {ComparisonBenchmark} benchmark Benchmark to use
 * @param {Locale} locale current locale
 */
export const benchmarkToBenchmarkPortfolio = (benchmark: ComparisonBenchmark, locale: Locale): BenchmarkPortfolio => ({
  _id: benchmark._id,
  name: formatLocalizedString(benchmark.name, locale) || '',
  holdings: [
    {
      type: COMPARISON_ITEM_TYPES.BENCHMARK,
      id: benchmark._id,
      name: benchmark.name || '',
      symbol: benchmark.ticker || '',
      weight: 100,
      currency: benchmark.currency || '',
      riskFreeId: benchmark.riskFreeId,
    },
  ],
  dates: {},
});
